import { useEffect, useState, useRef, DragEvent } from "react";
import { divideIndividuals } from "../../api/tournaments";
import { X, Plus } from "@mynaui/icons-react";
import { CheckCircle2, ChevronDown, Shuffle, Users } from "lucide-react";
import { Alert, Button, Dialog, EmptyState, Field, IconButton, Input, Tag } from "../../ui";
import "./styles.scss";

interface Player {
  id: string;
  firstName: string;
  lastName: string;
  gender: string;
  nationality: string;
}

interface TeamSlot {
  id: string;
  teamName: string;
  playerIds: string[];
}

interface DividePlayersDialogProps {
  isOpen: boolean;
  onClose: () => void;
  tournamentCategoryMappingId: string;
  players: Player[];
  onSave: () => void;
  /** Where Randomize's "Players per team" starts: the category's team size (2 when it has none). */
  defaultTeamSize?: number;
}

const isTouchDevice = () => "ontouchstart" in window || navigator.maxTouchPoints > 0;

const DividePlayersDialog = (props: DividePlayersDialogProps) => {
  const { isOpen, onClose, tournamentCategoryMappingId, players, onSave, defaultTeamSize } = props;

  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState<TeamSlot[]>([]);
  const [unassignedIds, setUnassignedIds] = useState<string[]>([]);
  const [draggedPlayerId, setDraggedPlayerId] = useState<string | null>(null);
  const [dragOverTarget, setDragOverTarget] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Tap-to-move (mobile)
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [collapsedTeams, setCollapsedTeams] = useState<Set<string>>(new Set());

  const teamNameInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      setTeams([]);
      setUnassignedIds(players.map((p) => p.id));
      setValidationError(null);
      setSelectedPlayerId(null);
      setTeamSize(defaultTeamSize ?? 2);
    }
  }, [isOpen, players, defaultTeamSize]);

  const getPlayerById = (id: string): Player | undefined => players.find((p) => p.id === id);

  const [showRandomize, setShowRandomize] = useState(false);
  const [teamSize, setTeamSize] = useState(defaultTeamSize ?? 2);

  const handleAddTeam = () => {
    setTeams((prev) => [...prev, { id: crypto.randomUUID(), teamName: "", playerIds: [] }]);
    setTimeout(() => teamNameInputRef.current?.focus(), 50);
  };

  const handleRandomize = () => {
    // Gather all player IDs (unassigned + in teams)
    const allIds = [...unassignedIds, ...teams.flatMap((t) => t.playerIds)];
    // Shuffle
    const shuffled = [...allIds].sort(() => Math.random() - 0.5);
    // Split into chunks
    const newTeams: TeamSlot[] = [];
    for (let i = 0; i < shuffled.length; i += teamSize) {
      const chunk = shuffled.slice(i, i + teamSize);
      newTeams.push({
        id: crypto.randomUUID(),
        teamName: `Team ${newTeams.length + 1}`,
        playerIds: chunk,
      });
    }
    setTeams(newTeams);
    setUnassignedIds([]);
    setCollapsedTeams(new Set());
    setShowRandomize(false);
    setValidationError(null);
  };

  const handleRemoveTeam = (teamId: string) => {
    const team = teams.find((t) => t.id === teamId);
    if (team) {
      setUnassignedIds((prev) => [...prev, ...team.playerIds]);
      setTeams((prev) => prev.filter((t) => t.id !== teamId));
    }
  };

  const handleTeamNameChange = (teamId: string, name: string) => {
    setTeams((prev) => prev.map((t) => (t.id === teamId ? { ...t, teamName: name } : t)));
  };

  // ── Move logic ──
  const moveToUnassigned = (playerId: string) => {
    setTeams((prev) => prev.map((t) => ({ ...t, playerIds: t.playerIds.filter((id) => id !== playerId) })));
    setUnassignedIds((prev) => (prev.includes(playerId) ? prev : [...prev, playerId]));
  };

  const moveToTeam = (playerId: string, teamId: string) => {
    setUnassignedIds((prev) => prev.filter((id) => id !== playerId));
    setTeams((prev) =>
      prev.map((t) => {
        if (t.id === teamId) return { ...t, playerIds: t.playerIds.includes(playerId) ? t.playerIds : [...t.playerIds, playerId] };
        return { ...t, playerIds: t.playerIds.filter((id) => id !== playerId) };
      })
    );
  };

  // ── Desktop drag handlers ──
  const handleDragStart = (e: DragEvent<HTMLDivElement>, playerId: string) => {
    setDraggedPlayerId(playerId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", playerId);
  };
  const handleDragEnd = () => { setDraggedPlayerId(null); setDragOverTarget(null); };
  const handleDragOver = (e: DragEvent<HTMLDivElement>, targetId: string) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverTarget(targetId); };
  const handleDragLeave = () => setDragOverTarget(null);
  const handleDropOnUnassigned = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); setDragOverTarget(null); if (draggedPlayerId) { moveToUnassigned(draggedPlayerId); setDraggedPlayerId(null); } };
  const handleDropOnTeam = (e: DragEvent<HTMLDivElement>, teamId: string) => { e.preventDefault(); setDragOverTarget(null); if (draggedPlayerId) { moveToTeam(draggedPlayerId, teamId); setDraggedPlayerId(null); } };

  // ── Mobile tap handlers ──
  const handlePlayerTap = (playerId: string) => {
    if (!isTouchDevice()) return;
    setSelectedPlayerId((prev) => (prev === playerId ? null : playerId));
  };

  const handleZoneTap = (targetId: string) => {
    if (!isTouchDevice() || !selectedPlayerId) return;
    if (targetId === "unassigned") {
      moveToUnassigned(selectedPlayerId);
    } else {
      moveToTeam(selectedPlayerId, targetId);
    }
    setSelectedPlayerId(null);
  };

  // ── Save ──
  const handleSave = async () => {
    if (unassignedIds.length > 0) { setValidationError("All players must be assigned to a team before saving."); return; }
    const emptyNameTeam = teams.find((t) => !t.teamName.trim());
    if (emptyNameTeam) { setValidationError("All teams must have a name."); return; }
    const emptyTeam = teams.find((t) => t.playerIds.length === 0);
    if (emptyTeam) { setValidationError(`Team "${emptyTeam.teamName}" has no players.`); return; }

    setValidationError(null);
    setLoading(true);
    try {
      await divideIndividuals({
        tournamentCategoryMappingId,
        teams: teams.map((t) => ({ teamName: t.teamName, playerIds: t.playerIds })),
      });
      onSave();
      onClose();
    } catch (error: any) {
      setValidationError(error?.response?.data?.message || "An error occurred while saving.");
    } finally {
      setLoading(false);
    }
  };

  const renderPlayerCard = (playerId: string, inZone: string) => {
    const player = getPlayerById(playerId);
    if (!player) return null;
    const isSelected = selectedPlayerId === player.id;
    const isDragging = draggedPlayerId === player.id;

    return (
      <div
        key={player.id}
        className={`divide-players__player ${isDragging ? "is-dragging" : ""} ${isSelected ? "is-selected" : ""}`}
        draggable={!isTouchDevice()}
        onDragStart={(e) => handleDragStart(e, player.id)}
        onDragEnd={handleDragEnd}
        onClick={() => handlePlayerTap(player.id)}
      >
        <div className="divide-players__player-name">
          {player.firstName} {player.lastName}
        </div>
        <div className="divide-players__player-meta">
          <span>{player.gender}</span>
          <span>{player.nationality}</span>
        </div>
        {isSelected && <div className="divide-players__selected-hint">Tap a team to move</div>}
      </div>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title="Divide Players into Teams"
      size="xl"
      busy={loading}
      className="divide-players"
      bodyClassName="divide-players__body"
      footer={
        <>
          <Button variant="ghost" onClick={() => onClose()}>
            Cancel
          </Button>
          <Button variant="primary" loading={loading} onClick={handleSave}>
            Save
          </Button>
        </>
      }
    >
      <div className="divide-players__toolbar">
        <div className="divide-players__randomize">
          <Button variant="tint" size="sm" icon={<Shuffle />} onClick={() => setShowRandomize(!showRandomize)}>
            Randomize
          </Button>
          {showRandomize && (
            <div className="divide-players__randomize-popover" onClick={(e) => e.stopPropagation()}>
              <Field label="Players per team" hint={`Creates ${Math.ceil(players.length / teamSize)} teams of ~${teamSize} players`}>
                {(id) => (
                  <div className="divide-players__randomize-row">
                    <Input
                      id={id}
                      type="number"
                      inputSize="sm"
                      block={false}
                      className="divide-players__randomize-input"
                      min={1}
                      max={players.length}
                      value={teamSize}
                      onChange={(e) => setTeamSize(Math.max(1, parseInt(e.target.value) || 1))}
                    />
                    <Button variant="primary" size="sm" className="divide-players__randomize-go" onClick={handleRandomize}>
                      Shuffle & Create
                    </Button>
                  </div>
                )}
              </Field>
            </div>
          )}
        </div>
      </div>

      {selectedPlayerId && (
        <Alert
          tone="info"
          className="divide-players__banner"
          action={
            <Button variant="ghost" size="sm" onClick={() => setSelectedPlayerId(null)}>
              Cancel
            </Button>
          }
        >
          Select a team to assign <strong>{getPlayerById(selectedPlayerId)?.firstName}</strong> to
        </Alert>
      )}

      <div className="divide-players__columns">
        {/* Unassigned */}
        <div className="divide-players__column divide-players__column--unassigned">
          <div className="divide-players__column-head">
            <h3>Unassigned ({unassignedIds.length})</h3>
          </div>
          <div
            className={`divide-players__pool ${dragOverTarget === "unassigned" ? "is-drag-over" : ""} ${selectedPlayerId && !unassignedIds.includes(selectedPlayerId) ? "is-tap-target" : ""}`}
            onDragOver={(e) => handleDragOver(e, "unassigned")}
            onDragLeave={handleDragLeave}
            onDrop={handleDropOnUnassigned}
            onClick={() => handleZoneTap("unassigned")}
          >
            {unassignedIds.length === 0 ? (
              <EmptyState compact icon={<CheckCircle2 />} title="All players assigned" />
            ) : (
              unassignedIds.map((id) => renderPlayerCard(id, "unassigned"))
            )}
          </div>
        </div>

        {/* Teams */}
        <div className="divide-players__column divide-players__column--teams">
          <div className="divide-players__column-head">
            <h3>Teams ({teams.length})</h3>
            <Button variant="tint" size="sm" icon={<Plus />} onClick={handleAddTeam}>
              Add Team
            </Button>
          </div>
          <div className="divide-players__teams">
            {teams.length === 0 ? (
              <EmptyState compact icon={<Users />} title={`Click "Add Team" to create team slots`} className="divide-players__teams-empty" />
            ) : (
              teams.map((team) => (
                <div key={team.id} className={`divide-players__team ${selectedPlayerId ? "is-tap-target" : ""}`}>
                  <div className="divide-players__team-head">
                    <IconButton
                      label={collapsedTeams.has(team.id) ? "Expand team" : "Collapse team"}
                      variant="plain"
                      size="sm"
                      className={`divide-players__team-toggle ${collapsedTeams.has(team.id) ? "is-collapsed" : ""}`}
                      icon={<ChevronDown />}
                      onClick={() => setCollapsedTeams((prev) => {
                        const next = new Set(prev);
                        next.has(team.id) ? next.delete(team.id) : next.add(team.id);
                        return next;
                      })}
                    />
                    <Input
                      ref={teamNameInputRef}
                      type="text"
                      inputSize="sm"
                      className="divide-players__team-name"
                      placeholder="Enter team name..."
                      value={team.teamName}
                      onChange={(e) => handleTeamNameChange(team.id, e.target.value)}
                    />
                    <Tag tone="spark">{team.playerIds.length}</Tag>
                    <IconButton label="Remove team" variant="danger" size="sm" icon={<X />} onClick={() => handleRemoveTeam(team.id)} />
                  </div>
                  {!collapsedTeams.has(team.id) && (
                    <div
                      className={`divide-players__team-zone ${dragOverTarget === team.id ? "is-drag-over" : ""} ${selectedPlayerId ? "is-tap-target" : ""}`}
                      onDragOver={(e) => handleDragOver(e, team.id)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDropOnTeam(e, team.id)}
                      onClick={() => handleZoneTap(team.id)}
                    >
                      {team.playerIds.length === 0 ? (
                        <div className="divide-players__drop-hint">
                          {selectedPlayerId ? "Tap here to assign" : "Drop players here"}
                        </div>
                      ) : (
                        team.playerIds.map((id) => renderPlayerCard(id, team.id))
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {validationError && (
        <Alert tone="danger" className="divide-players__error">
          {validationError}
        </Alert>
      )}
    </Dialog>
  );
};

export default DividePlayersDialog;
